import { useMemo, useState } from "react";

// components
import Loading from "../../components/common/LoaderCommon";
import TableComponent from "../../components/common/TableComponent";
import { ReusableCombobox } from "../../utils/components/ui/combobox";
import DownloadButton from "./DownloadFile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../utils/components/ui/dropdown-menu";

// service
import {
  useDownloadSalesReport,
  useGetSalesReport,
} from "../../service/reportsService";
import { useGetAllUsers } from "../../service/userService";
import { useInfiniteGetAllCustomer } from "../../service/customerService";

// icons
import { Search, X } from "lucide-react";
import PaginationComponent from "../../components/PaginationComponent";

function SalesReport() {
  const [searchKey, setSearchKey] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");

  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);
  const [userComboboxOpen, setUserComboboxOpen] = useState(false);

  const { data: customerData } = useInfiniteGetAllCustomer({
    pageSize: 10,
    pageNo: 0,
    customerName: customerSearch,
  });

  const { data: allUsersData } = useGetAllUsers({
    pageSize: 10,
    pageNo: 0,
    userName: userSearch,
  });

  const customerOptions =
    customerData?.pages
      ?.flatMap((page) => page.customerResponse || [])
      .map((customer: any) => ({
        value: String(customer.id),
        label: customer.username,
      })) || [];

  const downloadMutation = useDownloadSalesReport();

  const { data, isLoading } = useGetSalesReport({
    pageSize: pageSize,
    pageNo: currentPage,
    invoiceNumber: searchKey,
    startDate: startDate,
    endDate: endDate,
    customerId: selectedCustomer,
    staffId: selectedStaffId,
  });

  const userOptions = allUsersData?.userDetailsResponse;
  const totalPages = Math.ceil((data?.totalElements || 0) / pageSize);
  const purchaseReturnData = data?.salesReportResponse || [];

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const purchaseColumns = useMemo(
    () => [
      { header: "Invoice Number", accessor: "invoiceNumber" },
      { header: "Customer Name", accessor: "customerName" },
      { header: "Sales Date", accessor: "salesDate" },
      { header: "Total Amount", accessor: "totalAmount" },
      { header: "Vat Amount", accessor: "vatAmount" },
      { header: "Vat Included Total", accessor: "vatIncludedTotal" },
      { header: "Vat ExcludedTotal", accessor: "vatExcludedTotal" },
    ],
    [],
  );

  const isFilterActive =
    !!selectedCustomer ||
    !!searchKey ||
    !!startDate ||
    !!endDate ||
    !!selectedStaffId;

  const handleResetFilters = () => {
    setSelectedCustomer("");
    setCustomerSearch("");
    setCurrentPage(0);
    setSelectedStaffId("");
    setStartDate("");
    setEndDate("");
    setSearchKey("");
  };

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex flex-col gap-4">
          {/* Top Row */}
          <div className="flex gap-4 flex-wrap items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52627C]" />
              <input
                type="text"
                placeholder="Invoice number"
                onChange={(e) => setSearchKey(e.target.value)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] w-[300px]"
              />
            </div>
            <div className="min-w-[240px]">
              <ReusableCombobox
                options={customerOptions}
                value={selectedCustomer}
                onValueChange={(value) => {
                  setSelectedCustomer(value as string);
                }}
                open={customerComboboxOpen}
                setOpen={setCustomerComboboxOpen}
                placeholder="Select Customer"
                searchPlaceholder="Search customer..."
                emptyText="No customers found."
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                onInputChange={(val) => setCustomerSearch(val)}
              />
            </div>
            <div className="min-w-[240px]">
              <ReusableCombobox
                options={
                  userOptions?.map((user) => ({
                    value: String(user.id),
                    label: user.username,
                  })) || []
                }
                value={selectedStaffId}
                onValueChange={(value) => {
                  setSelectedStaffId(value as string);
                }}
                open={userComboboxOpen}
                setOpen={setUserComboboxOpen}
                placeholder="Select User"
                searchPlaceholder="Search user..."
                emptyText="No users found."
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                onInputChange={(val) => setUserSearch(val)}
              />
            </div>
          </div>

          {/* Bottom Row */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            </div>

            <div className="flex gap-2 items-end">
              {isFilterActive && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors"
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
              )}
              <DownloadButton
                endDate={endDate}
                startDate={startDate}
                mutation={downloadMutation}
                customerId={selectedCustomer}
                staffId={selectedStaffId}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table card */}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
          <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
            <p className="text-sm text-[#52627C]">
              <span className="font-semibold text-[#21314A]">
                {data?.totalElements}
              </span>{" "}
              Sales entries found
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#52627C]">Rows per page:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-8 rounded-[8px] border border-[#E5ECF8] bg-[#F5F8FD] px-3 text-sm text-[#23324D] focus:outline-none hover:bg-white"
                      type="button"
                    >
                      {pageSize}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {[10, 20, 30, 50, 100].map((size) => (
                      <DropdownMenuItem
                        key={size}
                        onSelect={() => handlePageSizeChange(size)}
                      >
                        {size}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
          <TableComponent
            caption="A list of your recent invoices."
            columns={purchaseColumns}
            data={purchaseReturnData || []}
          />
        </div>
      )}
    </div>
  );
}

export default SalesReport;
